import debug from "debug";
import * as THREE from "three";
const log = debug("app:connpoint");

/**
 * Manages connections between objects in a 3D scene, specifically handling "ConnectionPoint" objects.
 * @category Experience
 * @subcategory ObjectManagement
 */
class ConnectionManager {
  /**
   * Constructs a ConnectionManager instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this.newConnectionMade = false;
    this.connectedObjects = new Set();

    this.init();
  }

  cleanup() {
    console.log("Cleaning up ConnectionManager...");

    // Remove event listeners
    window.removeEventListener("mouseup", this.checkConnections);

    // Clear stored references
    this.connectedObjects.clear();
    this.scene = null;
    this.eventHub = null;
    this.experience = null;

    console.log("ConnectionManager cleanup complete.");
}

  /**
   * Initializes the connection manager by adding necessary event listeners.
   */
  init() {
    window.addEventListener("mouseup", this.checkConnections.bind(this));
  }

  /**
   * Checks for connections between "ConnectionPoint" objects in the scene.
   * Updates the connection status and visual appearance of objects based on collisions.
   */
  checkConnections() {
    const connectedMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });

    const connectionPoints = [];
    const originalMaterials = [];

    // Traverse the scene to find objects named "ConnectionPoint"
    this.scene.traverse((object) => {
      if (object.isGroup && object.name === "ConnectionPoint") {
        connectionPoints.push(object);
        originalMaterials.push(object.userData.originalMaterial);
      }
    });

    log("connectionpoints", connectionPoints);

    connectionPoints.forEach((object, index) => {
      // Reset to original material first
      object.material = originalMaterials[index];

      // Create a bounding box for the current object
      const bboxObject = new THREE.Box3().setFromObject(object);
      let isColliding = false;

      // Check for collision with other objects in the array
      for (let i = 0; i < connectionPoints.length; i++) {
        if (i !== index) {
          const otherObject = connectionPoints[i];
          const bboxOtherObject = new THREE.Box3().setFromObject(otherObject);

          if (bboxObject.intersectsBox(bboxOtherObject)) {
            const objType = object.userData.typeOfPort;
            const otherObjType = otherObject.userData.typeOfPort;
            let canConnect = false;

            // Determine if a connection is allowed
            if (objType === "HYBRID" || otherObjType === "HYBRID") {
              canConnect = true; // Hybrid can connect with any type
            } else if (
              (objType === "INPUT" && otherObjType === "OUTPUT") ||
              (objType === "OUTPUT" && otherObjType === "INPUT")
            ) {
              canConnect = true; // Input can connect with Output and vice versa
            }

            if (canConnect) {
              // Objects are touching, change color to green
              object.material = connectedMaterial; // Assuming connectedMaterial is green
              otherObject.material = connectedMaterial;

              // Set connection status
              object.userData.isConnected = true;
              otherObject.userData.isConnected = true;

              this.connectedObjects.add(object);
              this.connectedObjects.add(otherObject);
              this.newConnectionMade = true;

              isColliding = true;
            } else {
              // Reset connection status if they cannot connect
              object.userData.isConnected = false;
              otherObject.userData.isConnected = false;
            }

            break; // Stop checking once a collision is found
          }
        }
      }

      if (!isColliding) {
        // Reset connection status if no collision is detected
        object.userData.isConnected = false;

        // Remove from connectedObjects set
        this.connectedObjects.delete(object);

        // Reset the material to original
        object.material = originalMaterials[index];
      }
    });

    if (this.newConnectionMade) {
      // You can add additional logic here if necessary
      this.newConnectionMade = false;
    }

    // Check for any disconnections and clean up
    this.cleanDisconnectedObjects();
  }

  /**
   * Cleans up disconnected objects from the set of connected objects.
   * Ensures only valid connections remain tracked.
   */
  cleanDisconnectedObjects() {
    this.connectedObjects.forEach((object) => {
      if (!object.userData.isConnected) {
        this.connectedObjects.delete(object);
      }
    });
  }
}

export default ConnectionManager;
